
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

const ElegantTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  // Use muted gold for generic or brand color if specific
  const accentColor = '#bfa588'; 

  return (
    <div className={containerClass} style={{ backgroundColor: '#fcfcfc' }}>
       <div className="p-16 flex flex-col h-full items-center">
           {!hideHeader && (
             <>
               {/* Minimal Header */}
               <div className="w-full text-center mb-16">
                   <div className="mb-8 flex justify-center">
                       <LogoImage size="md" className="grayscale" />
                   </div>
                   <h1 className="font-serif text-4xl text-neutral-800 tracking-widest uppercase mb-4">{profile.name}</h1>
                   <div className="flex justify-center gap-6 text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-medium">
                       <span>{profile.address}</span>
                       <span>{profile.email}</span>
                   </div>
               </div>

               {/* Type & Meta */}
               <div className="w-full border-t border-b border-neutral-200 py-8 mb-16 flex justify-between items-start">
                   <div className="text-left w-1/3">
                       <p className="font-serif text-xs italic text-neutral-400 mb-2">Prepared For</p>
                       {client ? (
                           <div className="font-serif text-neutral-800">
                               <p className="text-xl leading-none mb-1">{client.name}</p>
                               <p className="text-sm text-neutral-500 italic">{client.company}</p>
                           </div>
                       ) : <span className="text-neutral-300">Client Name</span>}
                   </div>

                   <div className="w-1/3 text-center">
                       <h2 className="font-serif text-3xl italic text-neutral-800">{type}</h2>
                       <p className="text-[10px] uppercase tracking-widest text-neutral-400 mt-2">{title}</p>
                   </div>

                   <div className="w-1/3 text-right">
                       {metaData.map((m, i) => (
                           <div key={i} className="mb-2">
                               <span className="font-serif text-xs text-neutral-400 italic mr-3">{m.label}</span>
                               <span className="text-sm font-bold text-neutral-800 tracking-wide">{m.value}</span>
                           </div>
                       ))}
                   </div>
               </div>
             </>
           )}

           {/* Content */}
           <div className="w-full flex-1 elegant-table font-serif">
               {children}
           </div>

           {/* Footer */}
           <div className="mt-16 w-full text-center">
               <div className="w-12 h-px bg-neutral-300 mx-auto mb-6"></div>
               <p className="font-serif italic text-neutral-500 text-sm max-w-lg mx-auto leading-relaxed">
                   {footerNote}
               </p>
           </div>
       </div>
    </div>
  );
};

export default ElegantTemplate;
